import React, { useState, useEffect, use, useMemo } from 'react';
import { useAdminDashboard } from '@/hooks/admin/useAdminDashboard';
import { useVideoUsers } from '@/hooks/querry_hooks/admin/useAdminVideosQuery';
import AdminHeader from '../layout/AdminHeader';
import PendingUsersTable from '../tables/PendingUsersTable';
import ApprovedUsersTable from '../tables/ApprovedUsersTable';
import PendingUsersCardList from '../tables/mobile/PendingUsersCardList';
import ApprovedUsersCardList from '../tables/mobile/ApprovedUsersCardList';
import DashboardStats from '../cards/DashboardStats';
import SectionHeader from '../layout/SectionHeader';
import ErrorBanner from '@/components/ui/feedback/ErrorBanner';
import { useLoading } from '../../loading/LoadingContext';
import { DeviceContext } from '@/hooks/common/DeviceContext';
import Show from '@/utils/common/Show';
import { Match, Switch } from '@/utils/common/Switch';

/**
 * AdminDashboard - Main administrative dashboard for user management
 * 
 * Provides a comprehensive interface for administrators to manage user access
 * and approve registration requests. Features include user approval workflows,
 * statistics overview, and user management tools.
 * 
 * Features:
 * - Real-time user statistics dashboard
 * - Pending user approval interface
 * - Approved user management with search and filtering
 * - Error handling and loading states
 * - Responsive design for mobile and desktop
 * - Role-based access controls
 * 
 * Layout Structure:
 * - Header with navigation and admin controls
 * - Statistics cards showing key metrics
 * - Pending approvals table with action buttons
 * - Approved users table with management controls
 */
const AdminDashboard: React.FC = () => {
  const {
    pendingUsers,
    approvedUsers,
    currentAdmin,
    error,
    deletingUserId,
    approveUser,
    rejectUser,
    deleteUser,
    handleLogout,
    clearError,
    isDataLoading
  } = useAdminDashboard();

  const { showLoading, hideLoading } = useLoading();

  // Fetch video users data to get video counts per user
  const { data: videoUsers = [], isLoading: isLoadingVideoUsers } = useVideoUsers(true);

  // Create a mapping from user email to video count
  const userVideoCountMap = useMemo(() => {
    const map = new Map<string, number>();
    videoUsers.forEach((user: any) => {
      map.set(user.email, user.count || 0);
    });
    return map;
  }, [videoUsers]);

  // Show loading indicator during data fetching
  useEffect(() => {
    if (isDataLoading || isLoadingVideoUsers) {
      showLoading('Loading admin dashboard...');
    } else {
      hideLoading();
    }
  }, [isDataLoading, isLoadingVideoUsers, showLoading, hideLoading]);

  /**
   * Calculates total users across all statuses
   */
  const totalUsers = pendingUsers.length + approvedUsers.length;

  const [activeSection, setActiveSection] = useState('pending')
  const [videoSortOrder, setVideoSortOrder] = useState<'asc' | 'desc' | null>(null);
  const {isMobile} = use(DeviceContext) ?? {isMobile: false}

  // Sort approved users by video count
  const sortedApprovedUsers = useMemo(() => {
    if (!videoSortOrder) return approvedUsers;
    
    return [...approvedUsers].sort((a, b) => {
      const countA = userVideoCountMap.get(a.email) || 0;
      const countB = userVideoCountMap.get(b.email) || 0;
      return videoSortOrder === 'asc' ? countA - countB : countB - countA;
    });
  }, [approvedUsers, userVideoCountMap, videoSortOrder]);

  const handleVideoSortChange = (value: 'asc' | 'desc' | '') => {
    setVideoSortOrder(value === '' ? null : value);
  };

  return (
    <div className="w-full bg-transparent flex flex-col min-h-screen">
      {/* Header Navigation */}
      <AdminHeader
        currentAdmin={currentAdmin}
        onLogout={handleLogout}
      />

      {/* Main Dashboard Content */}
      <main className="flex-1 w-full px-4 sm:px-6 py-8 sm:py-12 bg-gray-50">
        {/* Error Display */}
        {error && (
          <ErrorBanner error={error} onDismiss={clearError} />
        )}

        {/* Page Header */}
        <div className="mb-8">
          <h1 
            className={`text-3xl font-semibold text-gray-900 mb-2 ${isMobile ? 'text-base mb-0' : ''}`}
            style={isMobile ? { lineHeight: '3px' } : undefined}
          >
            Admin Dashboard
          </h1>
          <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''}`}>
            Manage users and approve access to Picasso.
          </p>
        </div>

        {/* Dashboard Statistics */}
        <DashboardStats 
          pendingCount={pendingUsers.length}
          approvedCount={approvedUsers.length}
          totalCount={totalUsers}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />

        {/* Pending Users Section */}
        <div className={`mb-8 ${isMobile && activeSection !== 'pending' ? 'hidden' : ''}`}>
          <div
            className={`w-full rounded-lg overflow-hidden p-0 ${isMobile ? 'bg-transparent shadow-none border-0' : 'bg-white shadow-sm border border-gray-200'}`}
          >
            <Show when={!isMobile}>
              <SectionHeader 
                title="Pending Approvals"
                count={pendingUsers.length}
                description="Review and approve user access requests"
                icon={
                  <svg className="w-3 h-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
                bgColor="bg-orange-50"
            />
            </Show>
            <div className={`${isMobile ? 'p-0' : 'p-6'}`}>
              <Show when={!isMobile || (isMobile && activeSection === 'pending')}>
                <Switch>
                  <Match when={isMobile}>
                    <PendingUsersCardList
                      users={pendingUsers}
                      onApprove={approveUser}
                      onReject={rejectUser}
                      isMobile={isMobile}
                      userVideoCountMap={userVideoCountMap}
                    />
                  </Match>
                  <Match when={!isMobile}>
                    <PendingUsersTable
                      users={pendingUsers}
                      onApprove={approveUser}
                      onReject={rejectUser}
                      userVideoCountMap={userVideoCountMap}
                    />
                  </Match>
                </Switch>
              </Show>
            </div>
          </div>
        </div>

        {/* Approved Users Section */}
        <div className={`${isMobile && activeSection !== 'approved' ? 'hidden' : ''}`}>
          <div
            className={`w-full rounded-lg overflow-hidden p-0 ${isMobile ? 'bg-transparent shadow-none border-0' : 'bg-white shadow-sm border border-gray-200'}`}
          >
            <Show when={!isMobile}>
              <SectionHeader 
                title="Approved Users"
                count={sortedApprovedUsers.length}
                description="Currently active and approved users"
                icon={
                  <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
                bgColor="bg-green-50"
              />
            </Show>
            <div className={`${isMobile ? 'p-0' : 'p-6'}`}>
              <Show when={!isMobile || (isMobile && activeSection === 'approved')}>
                <Switch>
                  <Match when={isMobile}>
                    <ApprovedUsersCardList
                      users={sortedApprovedUsers}
                      onDeleteUser={deleteUser}
                      deleting={deletingUserId}
                      userVideoCountMap={userVideoCountMap}
                    />
                  </Match>
                  <Match when={!isMobile}>
                    <ApprovedUsersTable 
                      users={sortedApprovedUsers} 
                      onDeleteUser={deleteUser}
                      deleting={deletingUserId}
                      userVideoCountMap={userVideoCountMap}
                      videoSortOrder={videoSortOrder}
                      onToggleVideoSort={handleVideoSortChange}
                    />
                  </Match>
                </Switch>
              </Show>
            </div>
          </div>
        </div>

        {/* Video Extension Requests Section */}
      </main>
    </div>
  );
};

export default AdminDashboard;
